import { memo } from 'react'

import styled from 'styled-components'

import { Section as SectionType } from '../types/blueprint'

import { AddAnother } from './AddAnother'
import BlockGroup from './BlockGroup'
import { DisplayDescription } from './DisplayBlock.style'

import { useTemplateRenderer } from '@/context/templateRendererContext'

const SectionContainer = styled.div<{ breakBefore: boolean }>`
  width: 100%;
  break-before: ${({ breakBefore }) => (breakBefore ? 'page' : 'auto')};
  padding-bottom: 1rem;
  margin-bottom: 1rem;

  border-bottom: 1px solid ${({ theme }) => theme.colour.secondary._300};
`

const Title = styled.span`
  ${({ theme }) => theme.font.subheading01}
  margin-bottom: 0.25rem;
  display: block;
  white-space: pre-wrap;
`

type BodyProps = { marginTop: boolean }

const SectionBody = styled.div<BodyProps>`
  margin-top: ${({ marginTop }) => (marginTop ? '1rem' : '0')};

  > * + * {
    margin-top: 1.5rem;
  }
`

export const SectionCard: React.FC<Props> = ({ section, index, children }) => {
  const { renderOptions } = useTemplateRenderer()

  const { name, description } = section
  const hasTitleOrDescription = !!name || !!description
  const notFirstSection = index !== 0

  return (
    <SectionContainer
      breakBefore={notFirstSection && renderOptions.beginSectionOnNewPage}
    >
      {name && <Title>{name}</Title>}
      {description && <DisplayDescription>{description}</DisplayDescription>}
      <SectionBody marginTop={hasTitleOrDescription}>{children}</SectionBody>
    </SectionContainer>
  )
}

type Props = {
  section: SectionType
  index: number
  children?: React.ReactNode
}

const Section: React.FC<Props> = memo(({ section, index }) => {
  const { blockGroups } = section
  const { renderOptions, templateResponseData } = useTemplateRenderer()

  const SectionWrapper = renderOptions?.sectionRenderer ?? SectionCard

  if (renderOptions?.renderingInPdf && section.hideFromPdf) {
    return null
  }

  const isCapture = !renderOptions.renderingInPdf

  return (
    <SectionWrapper section={section} index={index}>
      {blockGroups?.map((blockGroup, i) => (
        <BlockGroup
          key={i}
          blockGroup={blockGroup}
          sectionId={section.id}
          index={i}
          noMargin={i === blockGroups.length - 1}
        />
      ))}
      {isCapture && section.repeatable && (
        <AddAnother
          type="SECTION"
          item={section}
          parentId={templateResponseData.id}
          parent={templateResponseData}
        />
      )}
    </SectionWrapper>
  )
})

export default Section
